import React, { useEffect, useState } from 'react';
import { keyframes } from '@emotion/react';
import { Link, useNavigate } from 'react-router-dom';
import ListErrors from './ListErrors';
import agent from '../agent';
import { connect } from 'react-redux';
import {
  UPDATE_FIELD_AUTH,
  REGISTER,
  REGISTER_PAGE_UNLOADED, BOTTOM_NAVIGATION_VISIBILITY
} from '../constants/actionTypes';
import useTranslation from "../customHooks/translations";
import ReCAPTCHA from "react-google-recaptcha";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ButtonGroup from "@mui/material/ButtonGroup";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { DEFAULT_THEME_COLOR, DEFAULT_GRADIENT, DEFAULT_GRADIENT_HOVER } from "../constants/theme";
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { capitalizeFirstLetter } from "../utils/commonUtils";
import { validatePassword } from "../utils/passwordUtils";

const inputSx = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.6)' },
    '&.Mui-focused fieldset': { borderColor: DEFAULT_THEME_COLOR },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
  '& .MuiInputLabel-root.Mui-focused': { color: DEFAULT_THEME_COLOR },
  '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.5)' },
};

const titleShine = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const Register = (props) => {
  const translation = useTranslation();
  const navigate = useNavigate();
  const [emailSent, setEmailSent] = useState(false);
  const [availableContexts, setAvailableContexts] = useState([]);
  const [selectedContext, setSelectedContext] = useState('wof');
  const [birthDate, setBirthDate] = useState('');
  const today = new Date().toISOString().slice(0, 10);

  const recaptchaRef = React.createRef();
  const [captchaError, setCaptchaError] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const handleClose = () => {
    setIsOpen(false);
    navigate('/login');
  };

  const changeEmail = ev => props.onChangeEmail(ev.target.value);
  const changePassword = ev => props.onChangePassword(ev.target.value);
  const changeUsername = ev => props.onChangeUsername(ev.target.value);
  const submitForm = (username, email, password, context, birthDate) => async ev => {
    ev.preventDefault();
    const token = recaptchaRef.current.getValue();
    if (!token) {
      setCaptchaError(true);
      return;
    }
    setCaptchaError(false);
    props.onSubmit(username, email, password, token, context, birthDate, '/login');
    props.onUnload();
  };

  useEffect(() => {
    props.setBottomNavigationVisible(false);

    const fetchData = async () => {
      const results = await agent.Freaks.availableContexts();
      setAvailableContexts(results);
    };

    fetchData();
  }, []);

  useEffect(() => {
    setEmailSent(false);
  }, [props.email, props.password, props.username]);

  const email = props.email;
  const password = props.password;
  const username = props.username;

  return (
    <>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0d1b2a 0%, #0d3d2e 100%)',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          py: 6,
        }}
      >
        <Typography
          variant="h1"
          sx={{ background: 'linear-gradient(90deg, #36c9a2 0%, #4facfe 35%, rgba(255,255,255,0.9) 50%, #4facfe 65%, #36c9a2 100%)', backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', fontWeight: 800, fontSize: { xs: '3rem', md: '4.5rem' }, letterSpacing: '-0.02em', mb: 4, textAlign: 'center', animation: `${titleShine} 4s linear infinite` }}
        >
          Freakland
        </Typography>
        <Box
          sx={{
            width: '100%',
            maxWidth: 480,
            backgroundColor: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 3,
            p: 4,
          }}
        >
          <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1, textAlign: 'center' }}>
            {translation.register.signUp}
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'center', mb: 3 }}>
            <Link to="/login" style={{ color: DEFAULT_THEME_COLOR, textDecoration: 'none' }}>
              {translation.register.haveAnAccount}
            </Link>
          </Typography>
          <ListErrors errors={props.errors} />
          <form onSubmit={submitForm(username, email, password, selectedContext, birthDate)}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label={translation.register.placeholders.username}
                value={props.username}
                onChange={changeUsername}
                sx={inputSx}
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={props.email}
                onChange={changeEmail}
                sx={inputSx}
              />
              <TextField
                fullWidth
                label={translation.register.placeholders.password}
                type="password"
                value={props.password}
                onChange={changePassword}
                sx={inputSx}
              />
              <TextField
                fullWidth
                required
                type="date"
                label={translation.register.placeholders.birthDate}
                value={birthDate}
                onChange={ev => setBirthDate(ev.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: today, 'data-testid': 'register-birth-date' }}
                helperText={translation.register.birthDateHint}
                FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.5)' } }}
                sx={{ ...inputSx, '& input::-webkit-calendar-picker-indicator': { filter: 'invert(1)' } }}
              />
              <FormControl fullWidth sx={inputSx}>
                <InputLabel id="context-label">{translation.register.defaultContext}</InputLabel>
                <Select
                  labelId="context-label"
                  value={selectedContext}
                  label={translation.register.defaultContext}
                  onChange={(event) => setSelectedContext(event.target.value)}
                >
                  {availableContexts?.map(availableContext => (
                    <MenuItem key={availableContext} value={availableContext}>
                      {translation.settings[`context${capitalizeFirstLetter(availableContext)}`]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {emailSent && !props.errors && (
                <Typography variant="body2" sx={{ color: DEFAULT_THEME_COLOR }}>
                  {translation.register.emailSent}
                </Typography>
              )}
              <FormGroup>
                <FormControlLabel
                  required
                  control={
                    <Checkbox sx={{ color: 'rgba(255,255,255,0.4)', '&.Mui-checked': { color: DEFAULT_THEME_COLOR } }} />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      {translation.register.termsOfService}{' '}
                      <Link to="/terms-of-service.html" target="_blank" style={{ color: DEFAULT_THEME_COLOR }}>
                        {translation.register.termsOfServiceTitle}
                      </Link>
                      {' '}{translation.register.termsOfServiceAnd}{' '}
                      <Link to="/privacy-policy.html" target="_blank" style={{ color: DEFAULT_THEME_COLOR }}>
                        {translation.register.privacyPolicy}
                      </Link>
                    </Typography>
                  }
                />
              </FormGroup>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECHAPTA_SITE_KEY}
              />
              {captchaError && (
                <Typography variant="caption" sx={{ color: '#f44336' }}>*reCAPTCHA</Typography>
              )}
              <Button
                fullWidth
                type="submit"
                size="large"
                variant="contained"
                disabled={props.inProgress}
                sx={{ background: DEFAULT_GRADIENT, color: 'white', fontWeight: 700, py: 1.5, fontSize: '1rem', '&:hover': { background: DEFAULT_GRADIENT_HOVER }, '&.Mui-disabled': { background: 'rgba(54,201,162,0.2)', color: 'rgba(255,255,255,0.5)' } }}
              >
                {translation.register.signUp}
              </Button>
            </Box>
          </form>
        </Box>
      </Box>
      <Modal
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 400, bgcolor: 'background.paper', boxShadow: 24, p: 4 }}>
          <Typography id="modal-modal-description">
            {translation.modals.confirmation.emailHasBeenSent}
          </Typography>
          <ButtonGroup sx={{ display: 'flex', marginTop: 1 }} orientation="horizontal">
            <Button
              onClick={handleClose}
              variant="contained"
              style={{ width: '100%', backgroundColor: DEFAULT_THEME_COLOR, color: 'white', alignSelf: 'flex-end', borderRadius: 4, marginRight: 1, marginBottom: 10 }}
            >
              {translation.modals.confirmation.ok}
            </Button>
          </ButtonGroup>
        </Box>
      </Modal>
    </>
  );
};

const mapStateToProps = state => ({ ...state.auth });

const mapDispatchToProps = dispatch => ({
  onChangeEmail: value =>
    dispatch({ type: UPDATE_FIELD_AUTH, key: 'email', value }),
  onChangePassword: value => {
    const errors = validatePassword(value);
    if (errors && errors.length) {
      dispatch({ type: UPDATE_FIELD_AUTH, error: true, key: 'password', value, payload: { errors } });
    } else {
      dispatch({ type: UPDATE_FIELD_AUTH, key: 'password', value });
    }
  },
  onChangeUsername: value =>
    dispatch({ type: UPDATE_FIELD_AUTH, key: 'username', value }),
  onSubmit: (username, email, password, token, context, birthDate, redirectTo) => {
    const payload = agent.Auth.register(username, email, password, token, context, birthDate);
    dispatch({ type: REGISTER, payload, redirectTo })
  },
  onUnload: () =>
    dispatch({ type: REGISTER_PAGE_UNLOADED }),
  setBottomNavigationVisible: isVisible => dispatch({
    type: BOTTOM_NAVIGATION_VISIBILITY,
    payload: isVisible
  }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Register);
